import { useState, useRef, useContext, useEffect } from "react";
import { FluentButton } from "../../../../../components/FluentUI/Fluent-Button";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import { Tooltip } from "@fluentui/react-components";
import { useFluentToast } from "../../../../../components/FluentUI/Fluent-Toast";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { LoginContext } from "../../../../../services/api/authentication-api";
import { useHistory } from "react-router-dom";
import { FluentInput } from "../../../../../components/FluentUI/Fluent-Input";
import PropTypes from "prop-types";
import {
  getLastUpdateTooltip,
  submitButton,
} from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import {
  getMyDealDetailColumns,
  handleMyDealExport,
  getInitialDetailState,
  saveDetailStateToStorage,
  clearDetailStateFromStorage,
  getFlattenedData,
  mapMyDealSummaryExportData,
  mapDealTrackerSummaryExportData,
  getDealTrackerSummaryColumns,
} from "../../levfin-helper";

import { SaveLevfinBankerDataByJuniorBanker } from "../../../../../services/api/dashboard-api";
import "../levfin-time-log.scss";
import LevfinBankerDetailGrid from "./levfin-detail-grid";
import LevfinBankerSummaryGrid from "./levfin-summary-grid";

const LevfinJuniorBankerMyDeals = ({ items }) => {
  const user = useContext(LoginContext);
  const [detailState, setDetailState] = useState(() => getInitialDetailState(user?.userID, "levfinJuniorBankerMyDeals"));
  const [isSaving, setIsSaving] = useState(false);
  const [data, setData] = useState(items.Deals || []);
  const [searchValue, setSearchValue] = useState("");
  const [summaryItem, setSummaryItem] = useState(items);
  const [originalOverallCapacity, setOriginalOverallCapacity] = useState(items?.OverallCapacity ?? null);
  const { showToast, FluentToast } = useFluentToast();
  const _detailExport = useRef(null);
  const _summaryExport = useRef(null);
  const searchValueRef = useRef(searchValue);
  const history = useHistory();
  const canCreate = user?.currentUser?.privileges?.["Projects"]?.Add ?? false;
  const canEdit = user?.currentUser?.privileges?.["Projects"]?.Edit ?? false;
  const [expandClass, setExpandClass] = useState("");
  const handleSearchChange = (e) => {
    setSearchValue(e.target.value.trimStart());
  };

  useEffect(() => {
    searchValueRef.current = searchValue;
    if (searchValue?.trim()) {
      clearDetailStateFromStorage(user?.userID, "levfinJuniorBankerMyDeals");
    }
  }, [searchValue]);


  const handleModifySearchFocus = () => {
    const input = document.getElementById("levfin-search-input");
    if (input) {
      input.focus();
    }
  };

  const handleDetailStateChange = (newState) => {
    const userId = user?.userID;
    setDetailState(newState);
    saveDetailStateToStorage(userId, newState, searchValueRef.current, "levfinJuniorBankerMyDeals");
  };

  const handleSubmit = async () => {
    const payload = {
      TaskId: summaryItem.OverallCapacityTaskId,
      OverallCapacity: summaryItem.OverallCapacity,
    }
    setIsSaving(true);
    let result = await SaveLevfinBankerDataByJuniorBanker(payload);
    if (result?.status === 200) {
      if (result?.data) {
        showToast("Success", "Overall Capacity submitted successfully", "success");
        setOriginalOverallCapacity(summaryItem.OverallCapacity);
        setSummaryItem((prev) => ({
          ...prev,
          LastUpdated: new Date().toUTCString(),
        }));
      } else {
        showToast("Error", "Unexpected response from server", "error");
      }
    } else {
      showToast("Error", "Failed to submit Overall Capacity", "error");
    }
    setIsSaving(false);
  }

  const hasCapacityEdit = originalOverallCapacity !== summaryItem?.OverallCapacity;
  const canSubmitCapacity = hasCapacityEdit && Boolean(summaryItem?.OverallCapacityTaskId);
  const isSubmitDisabled = isSaving || !canSubmitCapacity;
  const tooltipText = getLastUpdateTooltip(summaryItem?.LastUpdated);
  const submitBtn = submitButton(isSubmitDisabled, handleSubmit);


  const hasAnyFilter =
    detailState.filter &&
    Array.isArray(detailState.filter.filters) &&
    detailState.filter.filters.length > 0;

  const canReset = detailState.sort.length > 0 || hasAnyFilter;
  const resetDataState = () => {
    setSearchValue("");
    const resetState = { sort: [], filter: null };
    setDetailState(resetState);
    clearDetailStateFromStorage(user?.userID, "levfinJuniorBankerMyDeals");
  };

  const updateSummaryNumbers = (filteredData) => {
    const counts = {};

    // Initialize all category options with 0 (mirrors categoryOptions seeding in C#)
    if (items.CategoryOptions?.length) {
      items.CategoryOptions.forEach((option) => {
        if (option?.trim()) {
          counts[option.trim()] = 0;
        }
      });
    }

    // Count deals per GroupName (mirrors the foreach over deals in C#)
    filteredData.forEach((deal) => {
      const key = deal.GroupName?.trim() || "Uncategorized";
      counts[key] = (counts[key] || 0) + 1;
    });

    // Convert to array of { Key, Value } pairs to match KeyValuePair<string, int>[]
    const countByCategory = Object.entries(counts).map(([key, value]) => ({
      Key: key,
      Value: value,
    }));

    setSummaryItem((prev) => ({
      ...prev,
      Total: filteredData.length,
      NumberOfDeals: countByCategory,
    }));
  };

  const updateOverallCapacityDraft = (newVal) => {
    setSummaryItem((prev) => ({
      ...prev,
      OverallCapacity: newVal,
    }));
  }


  return (
    <>
      <div className={`levfin-time-log ${expandClass} dashboard-container-shadow`}>
        <div className="widget-header">
          <div className="header-left">
            <div className="main-title-badge-row">
              <div className="main-title">My Deals</div>
              <FluentInput
                id="levfin-search-input"
                placeholder="Search"
                type="text"
                value={searchValue}
                onChange={handleSearchChange}
                componentStyle="search-input"
                contentBefore={<FluentUiIcon name="SearchRegular" size={14} />}
                noKey
              />
            </div>
            <div className="sub-title">GIB/Leveraged Finance</div>
          </div>
          <div className="header-right">
            <Tooltip content="Reset Filters and Sorting">
              <FluentButton
                appearance="secondaryWithOutline"
                icon={
                  <FluentUiIcon
                    name="ArrowResetRegular"
                    color={canReset ? "#0F6CBD" : "#A0A0A0"}
                    size={20}
                    disabled={!canReset}
                  />
                }
                disabled={!canReset}
                onClick={resetDataState}
              />
            </Tooltip>

            <Tooltip content="Export Data">
              <FluentButton
                appearance="secondaryWithOutline"
                icon={
                  <FluentUiIcon
                    name="ArrowDownloadRegular"
                    color="#0F6CBD"
                    size={20}
                  />
                }
                onClick={() =>
                  handleMyDealExport({
                    summaryRef: _summaryExport,
                    detailRef: _detailExport,
                    summaryData: mapMyDealSummaryExportData(summaryItem),
                    detailData: getFlattenedData(data),
                    categoryOptions: items?.CategoryOptions || [],
                  })
                }
              />
            </Tooltip>
            <Tooltip content="Expand Widget">
              <span>
                <FluentButton
                  appearance="secondaryWithOutline"
                  icon={
                    <FluentUiIcon
                      name={
                        expandClass === "widgetExpanded"
                          ? "FullScreenMinimizeFilled"
                          : "FullScreenMaximizeFilled"
                      }
                      color={"#0F6CBD"}
                      size={20}
                    />
                  }
                  onClick={() => setExpandClass(expandClass === "" ? "widgetExpanded" : "")}
                />
              </span>
            </Tooltip>
            <FluentButton
              text="Create Deal"
              appearance="secondaryWithOutline"
              disabled={!canCreate}
              icon={
                <FluentUiIcon
                  name="FolderAddRegular"
                  color={canCreate ? "#0F6CBD" : "#A0A0A0"}
                  size={20}
                />
              }
              onClick={() => {
                if (canCreate) {
                  history.push({
                    pathname: "/projects/create",
                    state: { fromDashboard: true },
                  });
                }
              }}
            />

            {tooltipText ? (
              <Tooltip content={tooltipText} relationship="label" positioning="below">
                <div>{submitBtn}</div>
              </Tooltip>
            ) : (
              submitBtn
            )}
          </div>
        </div>
        <ExcelExport data={mapDealTrackerSummaryExportData(summaryItem)} ref={_summaryExport}>
          {getDealTrackerSummaryColumns(items?.CategoryOptions)}
        </ExcelExport>
        <ExcelExport data={getFlattenedData(data)} ref={_detailExport}>
          {getMyDealDetailColumns(data)}
        </ExcelExport>
        <LevfinBankerSummaryGrid
          summaryItem={summaryItem}
          onOverallCapacityChange={(newVal) => { updateOverallCapacityDraft(newVal) }}
        />
        <>
          <LevfinBankerDetailGrid
            data={data}
            categoryOptions={items.CategoryOptions}
            emptyCategories={items.EmptyCategories}
            canEdit={canEdit}
            canCreate={canCreate}
            detailState={detailState}
            onDetailStateChange={handleDetailStateChange}
            searchValue={searchValue}
            resetDataState={resetDataState}
            refineSearch={handleModifySearchFocus}
            updateSummaryNumbers={updateSummaryNumbers}
          />
          <div
            tabIndex={0}
            style={{ width: 0, height: 0, overflow: "hidden" }}
            onFocus={() => {
              const btn = document.getElementById("tb-submit-btn");
              if (btn) btn.focus();
            }}
          />
        </>
      </div>
      {FluentToast}
    </>
  );
};

export default LevfinJuniorBankerMyDeals;

LevfinJuniorBankerMyDeals.propTypes = {
  items: PropTypes.shape({
    Deals: PropTypes.arrayOf(PropTypes.object).isRequired,
    OverallCapacity: PropTypes.number,
    OverallCapacityTaskId: PropTypes.number,
    LastUpdated: PropTypes.string,
    CategoryOptions: PropTypes.arrayOf(PropTypes.string),
    EmptyCategories: PropTypes.array,
  }).isRequired,
};